import { useMemo, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { trackEvent } from "@/utils/ga4";

const VIDEO_ID = "ykKg4dT1DIw";
const CHANNEL_URL = "https://www.youtube.com/@sunlightpump";

const EnnosVideoSection = () => {
  const { copy } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const thumbnailUrl = `https://img.youtube.com/vi/${VIDEO_ID}/hqdefault.jpg`;

  const handlePlay = () => {
    setIsPlaying(true);
    trackEvent("ennos_video_play", { video_id: VIDEO_ID, location: "ennos_section" });
  };

  const handleChannelClick = () => {
    trackEvent("youtube_channel_click", { channel: "sunlightpump", location: "ennos_section" });
  };

  const videoSchemaJson = useMemo(
    () =>
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: copy.ennosVideo.title,
        description: copy.ennosVideo.body,
        thumbnailUrl,
        uploadDate: "2022-01-01",
        embedUrl: `https://www.youtube.com/embed/${VIDEO_ID}`,
        publisher: {
          "@type": "Organization",
          name: "Nexus Benin SAS",
          url: "https://www.nexus-benin.com",
        },
      }),
    [copy.ennosVideo.body, copy.ennosVideo.title, thumbnailUrl, VIDEO_ID],
  );

  return (
    <section className="section ennos-video-section" id="ennos-video" data-testid="ennos-video-section">
      <div className="container ennos-video-container" data-testid="ennos-video-container">
        <div className="ennos-video-text" data-testid="ennos-video-text">
          <p className="section-kicker" data-testid="ennos-video-kicker">
            {copy.ennosVideo.kicker}
          </p>
          <h2 className="section-title" data-testid="ennos-video-title">
            {copy.ennosVideo.title}
          </h2>
          <p className="section-subtitle" data-testid="ennos-video-subtitle">
            {copy.ennosVideo.subtitle}
          </p>
          <p className="section-text" data-testid="ennos-video-body">
            {copy.ennosVideo.body}
          </p>
          <a
            className="primary-button"
            href={CHANNEL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="ennos-video-cta"
            onClick={handleChannelClick}
          >
            {copy.ennosVideo.cta}
          </a>
          <p className="ennos-video-microcopy" data-testid="ennos-video-microcopy">
            {copy.ennosVideo.microcopy}
          </p>
        </div>
        <div className="ennos-video-card" data-testid="ennos-video-card">
          {isPlaying ? (
            <iframe
              title={copy.ennosVideo.title}
              className="ennos-video-frame"
              src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="ennos-video-iframe"
            />
          ) : (
            <button
              type="button"
              className="ennos-video-placeholder"
              onClick={handlePlay}
              data-testid="ennos-video-play"
              aria-label={copy.ennosVideo.playLabel}
            >
              <img src={thumbnailUrl} alt={copy.ennosVideo.thumbnailAlt} loading="lazy" />
              <span className="ennos-video-play-icon">▶</span>
              <span className="ennos-video-play-label">{copy.ennosVideo.playLabel}</span>
            </button>
          )}
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noreferrer"
            className="ennos-video-fallback"
            data-testid="ennos-video-fallback"
            onClick={handleChannelClick}
          >
            {copy.ennosVideo.fallback}
          </a>
        </div>
      </div>
      <script type="application/ld+json" data-testid="ennos-video-schema">
        {videoSchemaJson}
      </script>
    </section>
  );
};

export default EnnosVideoSection;
